import { createContext, useEffect, useState, useContext, useMemo } from "react";
import supabase from "../supabase-client";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext();
const NAV_LAYOUT_KEY = "gomove_nav_layout";

export const AuthContextProvider = ({ children }) => {
  const [session, setSession] = useState(undefined);
  const [claims, setClaims] = useState({}); // 🔹 store decoded JWT claims
  const [menuPrefs, setMenuPrefs] = useState({});
  // Last known layout, so a full page load doesn't flash the classic rail
  // while menu_preferences is still loading. Only a first-paint hint.
  const [navLayoutHint, setNavLayoutHint] = useState(() => {
    try {
      return localStorage.getItem(NAV_LAYOUT_KEY) || "classic";
    } catch {
      return "classic";
    }
  });
  const [menuPrefsLoaded, setMenuPrefsLoaded] = useState(false);
  const rememberLayout = (prefs) => {
    const layout = prefs?._layout === "polaris" ? "polaris" : "classic";
    setNavLayoutHint(layout);
    try {
      localStorage.setItem(NAV_LAYOUT_KEY, layout);
    } catch {
      /* storage blocked: the hint just isn't kept */
    }
  };

  const signInUser = async ({ email, password }) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) return { success: false, error: error.message };
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // scope "local": end only this browser's session. The default ("global")
  // revokes the user's sessions on every device, leaving the other tabs and
  // the iPhone app with a token that auth rejects as "Session not found"
  // (approve_odometer and every other edge function then answer 401).
  //
  // If the server already dropped the session ("session_not_found", e.g. after
  // a global logout from another device), auth-js 2.69 returns an error and
  // KEEPS the dead session in localStorage, so the user could never log out.
  // Clear it ourselves and reload into /login with a clean client.
  const signOutUser = async () => {
    const { error } = await supabase.auth.signOut({ scope: "local" });
    if (error) {
      clearStoredSession();
      setSession(null);
      window.location.assign("/login");
    }
    return { success: true };
  };

  // Load + validate the session on mount. A session persisted in localStorage
  // can be expired; if we hand an expired session to the app, PrivateRoute lets
  // the user in but every Supabase request goes out on the bare anon key
  // (silent anon fallback) instead of the authenticated JWT. So we fail closed:
  // refresh an expired session, and if the refresh fails, sign out to null so
  // PrivateRoute redirects to /login rather than running the app as anon.
  useEffect(() => {
    const isExpired = (s) =>
      !s?.expires_at || s.expires_at * 1000 <= Date.now();

    const loadSession = async () => {
      const { data } = await supabase.auth.getSession();
      const current = data.session;

      if (current && isExpired(current)) {
        const { data: refreshed, error } = await supabase.auth.refreshSession();
        if (error || !refreshed?.session) {
          const { error: outError } = await supabase.auth.signOut({ scope: "local" });
          if (outError) clearStoredSession();
          setSession(null);
          return;
        }
        setSession(refreshed.session);
        return;
      }

      setSession(current ?? null);
    };

    loadSession();

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session ?? null);
      }
    );

    return () => listener.subscription.unsubscribe();
  }, []);

  // 🔹 Decode JWT claims when session changes
  useEffect(() => {
    if (session?.access_token) {
      try {
        const decoded = jwtDecode(session.access_token);
        setClaims(decoded);
      } catch (err) {
        console.error("Failed to decode JWT:", err);
        setClaims({});
      }
    } else {
      setClaims({});
    }
  }, [session]);

  // 🔹 Helper: check for a specific permission
  const hasPermission = (permission) => {
    const permissions = claims?.permissions || [];
    return permissions.includes(permission);
  };

  // 🔹 Load the user's menu_preferences blob whenever the session changes.
  const reloadMenuPrefs = async () => {
    if (!session?.user?.id) {
      setMenuPrefs({});
      return;
    }
    const { data } = await supabase
      .from("users")
      .select("menu_preferences")
      .eq("id", session.user.id)
      .maybeSingle();
    setMenuPrefs(data?.menu_preferences || {});
    rememberLayout(data?.menu_preferences);
    setMenuPrefsLoaded(true);
  };

  // 🔹 Classic icon rail ("classic", the default) or Polaris navigation
  // ("polaris"). Stored per user next to the menu preferences.
  const setNavLayout = async (layout) => {
    if (!session?.user?.id) return;
    const next = { ...menuPrefs, _layout: layout };
    setMenuPrefs(next);
    rememberLayout(next);
    const { error } = await supabase
      .from("users")
      .update({ menu_preferences: next })
      .eq("id", session.user.id);
    if (error) await reloadMenuPrefs();
  };

  useEffect(() => {
    reloadMenuPrefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  // 🔹 Memoize the context value for performance
  const value = useMemo(
    () => ({
      session,
      claims,
      signInUser,
      signOutUser,
      hasPermission,
      menuPrefs,
      reloadMenuPrefs,
      setNavLayout,
      navLayout: menuPrefsLoaded ? (menuPrefs?._layout === "polaris" ? "polaris" : "classic") : navLayoutHint,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session, claims, menuPrefs, menuPrefsLoaded, navLayoutHint]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// The supabase-js session lives in localStorage as sb-<project>-auth-token.
function clearStoredSession() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith("sb-") && k.includes("-auth-token"))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* storage unavailable */
  }
}

export const UserAuth = () => useContext(AuthContext);
